import type { OperationItem } from './types';

// Lowercase add-button subitem kinds (mapped to a shared-form category by the
// subitem-form adapter). "other" exists for parity but has no shared rich form.
export type SubitemKind = 'transport' | 'guide' | 'ticket' | 'other';

type EligibilityItem = Pick<
  OperationItem,
  'contentCategory' | 'contentSubcategory' | 'parentId'
>;

// Transportation subcategories that carry their own crew — legacy still lets
// them host Transport/Ticket subitems.
const SELF_TRANSPORT = ['Ferry', 'Flight', 'Train', 'Cruise'];

const isSelfTransport = (item: EligibilityItem): boolean =>
  item.contentCategory === 'Transportation' &&
  SELF_TRANSPORT.includes(item.contentSubcategory ?? '');

const inCategories = (item: EligibilityItem, cats: string[]): boolean =>
  cats.includes(item.contentCategory ?? '');

// Which sub-item types can be added under this item, mirroring legacy's
// `subitemButtons`. The whole button group is gated by the transport
// condition (legacy `if (!shouldShowTransportButton) return null`): the row
// must be Activity/Attraction/Destination/Dining/Venue, or a self-crewed
// Transportation (Ferry/Flight/Train/Cruise). Otherwise NO buttons — not even
// "Other". Sub-items (parentId set) host none.
export const allowedSubitemTypes = (item: EligibilityItem): SubitemKind[] => {
  if (item.parentId) return [];

  const showsButtons =
    isSelfTransport(item) ||
    inCategories(item, [
      'Activity',
      'Attraction',
      'Destination',
      'Dining',
      'Venue',
    ]);
  if (!showsButtons) return [];

  // Legacy render order: Transport, Ticket, Guide, Other.
  const kinds: SubitemKind[] = ['transport'];
  if (
    isSelfTransport(item) ||
    inCategories(item, ['Activity', 'Attraction', 'Venue'])
  ) {
    kinds.push('ticket');
  }
  if (inCategories(item, ['Activity', 'Attraction', 'Venue'])) {
    kinds.push('guide');
  }
  kinds.push('other');
  return kinds;
};
